import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {Image} from '../../atoms/Image/Image';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {ImageListItems, LogoItem} from './types';

export interface LogoListProps {
  items: ImageListItems;
  className?: string;
  padded?: boolean;
  monochrome?: boolean;
  withBorder?: boolean;
}

export const LogoList: FC<LogoListProps> = ({
  className,
  items,
  padded,
  monochrome,
  withBorder,
}) => {
  return (
    <Wrapper className={className}>
      {items.map((item, index) => (
        <ConditionalLinkWrapper
          data-testid="logoTile"
          key={index}
          item={item}
          padded={padded}
        >
          <StyledImage
            src={item.image.url}
            alt={item.image.description}
            loading="lazy"
            breakpoints={[]}
            ratio={'2:1'}
            widths={[150]}
            importance="low"
            disableFixedRatio
            $monochrome={monochrome}
            $withBorder={withBorder}
          />
        </ConditionalLinkWrapper>
      ))}
    </Wrapper>
  );
};

const ConditionalLinkWrapper: React.FC<
  React.PropsWithChildren<{
    ['data-testid']?: string;
    item: LogoItem;
    padded?: boolean;
  }>
> = ({children, item, 'data-testid': dataTestId, padded}) => {
  if (item.link) {
    return (
      <LogoTileLink
        data-testid={dataTestId}
        to={item.link.url}
        $padded={padded}
        aria-label={undefined}
      >
        {children}
      </LogoTileLink>
    );
  }

  return (
    <LogoTileSpan data-testid={dataTestId} $padded={padded}>
      {children}
    </LogoTileSpan>
  );
};

const Wrapper = styled.span`
  display: flex;
  width: 100%;
`;

const logoTileStyles = `
  display: flex;
  align-items: center;
  flex: 0;
`;

const LogoTileLink = styled(UnstyledLinkLinaria)<{
  $padded?: boolean;
}>`
  ${logoTileStyles};
  padding: ${props => (props.$padded ? '1rem 0.5rem' : '0')};
`;

const LogoTileSpan = styled.span<{
  $padded?: boolean;
}>`
  ${logoTileStyles};
  padding: ${props => (props.$padded ? '1rem 0.5rem' : '0')};
`;

const StyledImage = styled(Image)<{
  $monochrome?: boolean;
  $withBorder?: boolean;
}>`
  max-height: 2rem;
  min-width: 2.75rem;
  max-width: 8.125rem;

  border: ${({$withBorder}) =>
    $withBorder ? `1px solid ${COLORS.Grey3}` : ''};
  border-radius: ${({$withBorder}) => ($withBorder ? '0.25rem' : '')};

  filter: ${({$monochrome}) => ($monochrome ? 'grayscale(1)' : '')};
  mix-blend-mode: ${({$monochrome}) => ($monochrome ? 'darken' : '')};
`;
